import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { channelsAPI, whatsappAPI, capsulesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Radio, Wifi, WifiOff, QrCode, Plus, Trash2, Loader2, Phone,
  MessageSquare, RefreshCw, Settings, CheckCircle2, AlertCircle, Save, ChevronDown, ChevronRight, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import HelpButton from '@/components/HelpButton';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import QuienAtiendeElCanal from '@/components/channels/QuienAtiendeElCanal';
import IngresoDeM365 from '@/components/channels/IngresoDeM365';
import AtencionDelCanal, { nadieLoAtiende } from '@/components/channels/AtencionDelCanal';
import CuentaConectada from '@/components/channels/CuentaConectada';
import { fechaYHora } from '../lib/fechas';

const CHANNEL_TYPES = {
  whatsapp: { label: 'WhatsApp', icon: Phone, color: 'bg-green-500' },
  teams: { label: 'Microsoft Teams', icon: MessageSquare, color: 'bg-purple-500' },
  email: { label: 'Microsoft Email', icon: Mail, color: 'bg-indigo-500' },
  webchat: { label: 'WebChat', icon: MessageSquare, color: 'bg-brand-primary' },
};

const STATUS_MAP = {
  connected: { label: 'Conectado', icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-50' },
  degraded: { label: 'Degradado', icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
  qr_ready: { label: 'Esperando QR', icon: QrCode, color: 'text-amber-500', bg: 'bg-amber-50' },
  pairing_confirm_phone: { label: 'Confirmar en telefono', icon: Phone, color: 'text-blue-600', bg: 'bg-blue-50' },
  connecting: { label: 'Conectando...', icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-50' },
  disconnected: { label: 'Desconectado', icon: WifiOff, color: 'text-zinc-400', bg: 'bg-zinc-50' },
};

/**
 * El selector de casilla, **uno solo para los dos formularios**.
 *
 * El 2026-08-22 se puso el desplegable en la pantalla de edicion y el formulario de creacion
 * quedo con un campo de texto libre. Diego creo un canal escribiendo la direccion a mano, con el
 * dominio equivocado, y **nada lo advirtio**: el canal se guardo igual y el fallo aparecio recien
 * al intentar mandar, como `ErrorInvalidUser` de Graph.
 *
 * Es la tercera vez en el mismo dia que un arreglo aplicado donde aparece el sintoma deja vivo el
 * otro lugar. Por eso es un componente y no dos copias.
 *
 * **Escribir a mano sigue existiendo** —una integracion caida no puede dejar sin configurar el
 * correo— pero ahora dice que se esta arriesgando.
 */
/**
 * Lo ultimo que este canal pudo enviar de verdad.
 *
 * **"Conectado" no quiere decir que salga nada.** El 2026-08-24 el puente contesto `open` en
 * milisegundos durante horas mientras cada envio se colgaba para siempre: la pantalla se veia
 * verde, el Monitor mostraba la respuesta del agente, y del otro lado no llego nada. Hubo que
 * llegar al log del contenedor para darse cuenta. Un fallo que se disfraza de exito es peor que
 * uno visible, porque nadie lo busca.
 *
 * Por eso el dato que decide si el canal sirve —cuando salio el ultimo mensaje— se muestra al
 * lado del estado, y no en un log que nadie mira.
 */
function UltimoEnvio({ salud }) {
  const ok = salud?.last_send_ok_at;
  const error = salud?.last_send_error;
  if (!ok && !error) {
    return (
      <p className="text-[10px] text-zinc-400" data-testid="wa-ultimo-envio">
        Todavia no salio ningun mensaje desde que el puente arranco. Conectado no quiere decir
        que salga: se sabe con el primero.
      </p>
    );
  }
  const cuando = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const min = Math.round((Date.now() - d.getTime()) / 60000);
    if (min < 1) return 'recien';
    if (min < 60) return `hace ${min} min`;
    if (min < 1440) return `hace ${Math.round(min / 60)} h`;
    return fechaYHora(d);
  };
  return (
    <div className="text-[10px] space-y-0.5" data-testid="wa-ultimo-envio">
      {ok && (
        <p className="text-zinc-500">
          Ultimo mensaje enviado <b>{cuando(ok)}</b>
          <span className="text-zinc-400 font-mono ml-1">{fechaYHora(ok)}</span>
        </p>
      )}
      {error && (
        <p className="text-red-600" title={error.message}>
          Ultimo error al enviar {cuando(error.at)}: {error.message}
        </p>
      )}
    </div>
  );
}

function SelectorDeCasilla({ channelType, channelId, valor, onChange, testId }) {
  const [buzones, setBuzones] = useState([]);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [aMano, setAMano] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const res = await channelsAPI.listarBuzones(channelType, channelId);
      const lista = res.data?.buzones || [];
      setBuzones(lista);
      // `aviso` avisa que la lista se corto: un total que coincide con el tope se lee como
      // completo, y quien no encuentre su casilla busca el error donde no esta.
      setError(res.data?.error || res.data?.aviso || '');
      const guardada = (valor || '').toLowerCase();
      // Si lo guardado no esta en la lista se muestra a mano en vez de desaparecer: una casilla
      // que se borra sola del formulario es peor que una que no figura entre las opciones.
      setAMano(!res.data?.ok || (!!guardada && !lista.some(b => b.upn === guardada)));
    } catch {
      setBuzones([]);
      setError('No se pudo consultar la lista de casillas.');
      setAMano(true);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [channelId, channelType]);

  return (
    <div>
      {!aMano ? (
        <select
          data-testid={testId || 'ms-channel-account-select'}
          value={valor || ''}
          onChange={e => onChange(e.target.value)}
          disabled={cargando}
          className="w-full rounded-sm text-xs font-mono h-9 border border-zinc-300 bg-white px-2 dark:bg-zinc-900 dark:border-zinc-700"
        >
          <option value="">{cargando ? 'Buscando casillas...' : 'Elegi una casilla'}</option>
          {buzones.map(b => (
            <option key={b.upn} value={b.upn} disabled={!!b.tomada_por}>
              {b.nombre} — {b.upn}
              {b.tomada_por ? ` (ya la usa "${b.tomada_por}")` : ''}
              {b.habilitada === false ? ' (cuenta deshabilitada)' : ''}
            </option>
          ))}
        </select>
      ) : (
        <Input
          data-testid="ms-channel-account-input"
          value={valor || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="ej: Del.IA@pritty.com.ar"
          className="rounded-sm text-xs font-mono h-9"
        />
      )}
      <div className="mt-1 flex items-center justify-between gap-2">
        <p className="text-[10px] text-zinc-500">
          {error
            ? error
            : aMano
              ? 'Escrita a mano: si no figura en el directorio, no va a poder mandar ni recibir.'
              : 'Solo se listan cuentas con casilla de correo.'}
        </p>
        <button type="button" onClick={() => (aMano ? cargar() : setAMano(true))}
          className="text-[10px] underline text-zinc-500 hover:text-zinc-800 shrink-0">
          {aMano ? 'Elegir de la lista' : 'Escribir a mano'}
        </button>
      </div>
    </div>
  );
}


function MicrosoftChannelAccountPanel({ channel, onChannelUpdated }) {
  const guardadaCuenta = channel?.config?.microsoft_account || '';
  const guardadoRemitente = !!channel?.config?.remitente_de_avisos;
  const [microsoftAccount, setMicrosoftAccount] = useState(guardadaCuenta);
  const [saving, setSaving] = useState(false);
  const [esRemitenteDeAvisos, setEsRemitenteDeAvisos] = useState(guardadoRemitente);

  useEffect(() => {
    setMicrosoftAccount(channel?.config?.microsoft_account || '');
  }, [channel?.channel_id, channel?.config?.microsoft_account]);

  // **La casilla de verificacion tambien se resincroniza al cambiar de canal.** Sin esto se
  // quedaba con el valor del canal anterior: mirar uno marcado y despues abrir otro dejaba la
  // marca puesta, y guardar la escribia en el equivocado. Es el mismo defecto que el campo de
  // arriba ya tenia resuelto — lo escribi al lado y me olvide de copiarle la mitad que importa.
  useEffect(() => {
    setEsRemitenteDeAvisos(!!channel?.config?.remitente_de_avisos);
  }, [channel?.channel_id, channel?.config?.remitente_de_avisos]);

  const hayCambios = microsoftAccount.trim() !== guardadaCuenta || esRemitenteDeAvisos !== guardadoRemitente;

  const handleSaveAccount = async () => {
    if (!microsoftAccount.trim()) {
      toast.error('Elegí la cuenta de Microsoft del canal');
      return;
    }
    setSaving(true);
    try {
      const res = await channelsAPI.update(channel.channel_id, {
        config: {
          ...channel.config,
          microsoft_account: microsoftAccount.trim(),
          remitente_de_avisos: esRemitenteDeAvisos,
        },
      });
      toast.success('Cuenta guardada');
      onChannelUpdated?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar la cuenta');
    } finally {
      setSaving(false);
    }
  };

  // El estado y los botones de Graph (suscribir, sincronizar) se mudaron a «Cómo entra lo
  // nuevo» (`components/channels/IngresoDeM365.jsx`, 2026-10-19). Aca queda solo **que casilla**
  // usa el canal.
  return (
    <div className="border border-zinc-200 rounded-sm p-3 space-y-3 bg-zinc-50/70" data-testid="ms-channel-account-panel">
      {/* El nombre del canal, arriba de todo. Un formulario que no dice sobre que objeto opera
          deja que alguien edite el equivocado creyendo que edita otro. */}
      <div>
        <h5 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-600 flex items-center gap-1">
          <Mail className="w-3.5 h-3.5" /> Cuenta Microsoft de <span className="text-indigo-700 normal-case">{channel?.name || 'este canal'}</span>
        </h5>
        <p className="text-[10px] text-zinc-500 mt-1">
          La casilla que atiende este canal. Las credenciales de Azure están en <strong>Conexiones &rarr; microsoft-365</strong>.
        </p>
      </div>
      <div>
        <Label className="text-[10px] uppercase text-zinc-500">Cuenta (UPN)</Label>
        <SelectorDeCasilla
          channelType={channel?.channel_type}
          channelId={channel?.channel_id}
          valor={microsoftAccount}
          onChange={setMicrosoftAccount}
        />
        {/* Elegirla de una lista no es comodidad: la lista blanca de buzones del punto 8-bis
            sale de lo que los canales declaran, asi que un UPN tipeado mal deja una entrada que
            no corresponde a nada — y si cae justo sobre una casilla real, es un permiso dado
            por accidente. */}
      </div>

      {/* **De donde salen los avisos internos.** Sin esto, el sistema usa cualquier canal de la
          capsula que pueda entregar — que es lo correcto por omision, porque un producto recien
          instalado tiene que avisar igual. Esto es para cuando alguien quiere una casilla
          aparte: tipicamente una de comunicaciones, distinta de la que atiende a la gente. */}
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          id={`remitente-${channel?.channel_id}`}
          data-testid="ms-channel-remitente"
          checked={esRemitenteDeAvisos}
          onChange={e => setEsRemitenteDeAvisos(e.target.checked)}
          className="mt-0.5"
        />
        <Label htmlFor={`remitente-${channel?.channel_id}`}
               className="text-[11px] text-zinc-600 font-normal cursor-pointer leading-snug">
          Usar esta casilla para los <strong>avisos internos del equipo</strong> (tarea asignada,
          caso traspasado, cola de un rol).
          <span className="block text-[10px] text-zinc-400">
            Si no marcás ninguna, se usa cualquier canal de la cápsula que pueda entregar.
          </span>
        </Label>
      </div>
      <Button size="sm" onClick={handleSaveAccount} disabled={saving || !hayCambios} data-testid="ms-channel-account-save">
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
        Guardar
      </Button>
    </div>
  );
}

export default function ChannelsPage() {
  const { user } = useAuth();
  const [capsuleList, setCapsuleList] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [waPairingPhase2, setWaPairingPhase2] = useState(false);
  const [qrPolling, setQrPolling] = useState(false);
  const qrIntervalRef = useRef(null);
  const waResetCooldownTimerRef = useRef(null);
  const [waResetCooldown, setWaResetCooldown] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
  const [waDiagLoading, setWaDiagLoading] = useState(false);
  const [waDiagResult, setWaDiagResult] = useState(null);
  const [waGroupPolicy, setWaGroupPolicy] = useState('deny_all');
  const [waAllowedGroupJidsText, setWaAllowedGroupJidsText] = useState('');
  const [waPolicySaving, setWaPolicySaving] = useState(false);
  const [waItMode, setWaItMode] = useState('immediate');
  const [waItIdleMs, setWaItIdleMs] = useState('3500');
  const [waItMaxBatchMs, setWaItMaxBatchMs] = useState('30000');
  const [waItMerge, setWaItMerge] = useState('concat_newlines');
  const [waItGroupTrigger, setWaItGroupTrigger] = useState('every_message');
  const [waItDebounceBackend, setWaItDebounceBackend] = useState('memory');
  const [waInboundHelpOpen, setWaInboundHelpOpen] = useState(false);

  const [newChannel, setNewChannel] = useState({
    name: '', channel_type: 'whatsapp', config: { phone_number: '', capsule_id: '', microsoft_account: '', group_policy: 'deny_all', allowed_group_jids: [] }
  });

  const loadChannels = useCallback(async () => {
    try {
      const res = await channelsAPI.list();
      setChannels(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  // **La vuelta de conectar una cuenta de Microsoft** (2026-10-19): el backend redirige acá con
  // `?canal=…&cuenta_conectada=ok|error&motivo=…`. Se muestra el resultado, se abre el canal y se
  // limpia la URL para que recargar no repita el aviso.
  const vueltaProcesada = useRef(false);
  useEffect(() => {
    if (vueltaProcesada.current || loading) return;
    const params = new URLSearchParams(window.location.search);
    const resultado = params.get('cuenta_conectada');
    if (!resultado) return;
    vueltaProcesada.current = true;
    const canal = channels.find((c) => c.channel_id === params.get('canal'));
    if (canal) setSelectedChannel(canal);
    if (resultado === 'ok') toast.success('Cuenta conectada: Teams ya lee y contesta con ella');
    else toast.error(params.get('motivo') || 'No se pudo conectar la cuenta', { duration: 15000 });
    window.history.replaceState(null, '', window.location.pathname);
  }, [loading, channels]);

  useEffect(() => {
    const tid = user?.tenant_id;
    if (!tid) return;
    capsulesAPI.list(tid).then((r) => setCapsuleList(r.data || [])).catch(() => setCapsuleList([]));
  }, [user?.tenant_id]);

  useEffect(() => {
    if (!selectedChannel || selectedChannel.channel_type !== 'whatsapp') return;
    const cfg = selectedChannel.config || {};
    setWaGroupPolicy(cfg.group_policy || 'deny_all');
    setWaAllowedGroupJidsText(Array.isArray(cfg.allowed_group_jids) ? cfg.allowed_group_jids.join('\n') : '');
    const it = cfg.inbound_turn || {};
    setWaItMode(it.mode || 'immediate');
    setWaItIdleMs(String(it.idle_quiet_ms ?? 3500));
    setWaItMaxBatchMs(String(it.max_batch_window_ms ?? 30000));
    setWaItMerge(it.merge_user_text || 'concat_newlines');
    setWaItGroupTrigger(it.group_trigger || 'every_message');
    setWaItDebounceBackend(it.debounce_backend || 'memory');
  }, [selectedChannel]);

  // Cleanup QR polling on unmount
  useEffect(() => {
    return () => {
      if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
      if (waResetCooldownTimerRef.current) clearTimeout(waResetCooldownTimerRef.current);
    };
  }, []);

  const handleCreateChannel = async () => {
    if (!newChannel.name.trim()) { toast.error('Nombre requerido'); return; }
    if (!newChannel.config?.capsule_id?.trim()) {
      toast.error('Selecciona la cápsula del canal (obligatorio)');
      return;
    }
    try {
      const res = await channelsAPI.create(newChannel);
      setChannels(prev => [...prev, res.data]);
      // **Y se selecciona.** Sin esto el canal aparece en la lista y el panel de la derecha
      // sigue mostrando el anterior: uno cree que esta configurando el que acaba de crear y en
      // realidad esta editando otro. El 2026-08-22 eso le cambio la casilla al canal que atendia
      // el correo, y no hubo forma de notarlo hasta que dejo de llegar.
      setSelectedChannel(res.data);
      setShowCreate(false);
      setNewChannel({ name: '', channel_type: 'whatsapp', config: { phone_number: '', capsule_id: '', group_policy: 'deny_all', allowed_group_jids: [] } });
      toast.success('Canal creado');
    } catch (err) { toast.error('Error creando canal'); }
  };

  const handleDeleteChannel = async (channelId) => {
    setConfirmState({
      open: true,
      title: 'Eliminar canal',
      description: `Se eliminara el canal ${channelId}.`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          await channelsAPI.delete(channelId);
          setChannels(prev => prev.filter(c => c.channel_id !== channelId));
          if (selectedChannel?.channel_id === channelId) setSelectedChannel(null);
          toast.success('Canal eliminado');
        } catch (err) { toast.error('Error eliminando canal'); }
      },
    });
  };

  const runWaIntegrationDiag = async () => {
    setWaDiagLoading(true);
    setWaDiagResult(null);
    try {
      const res = await whatsappAPI.integrationStatus();
      setWaDiagResult(res.data);
      toast.success('Diagnostico obtenido. Envia un mensaje WA y vuelve a pulsar: debe subir upsert_events en el bridge.');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d ? JSON.stringify(d) : 'Sin permiso o error de red (admin/supervisor)');
    } finally {
      setWaDiagLoading(false);
    }
  };

  const handleConnect = async (channel, opts = {}) => {
    const resetPairing = !!opts.resetPairing;
    setSelectedChannel(channel);
    setQrData(null);
    setWaPairingPhase2(false);
    try {
      toast.info(resetPairing ? 'Reiniciando emparejamiento (servidor)...' : 'Iniciando sesion WhatsApp...');
      await whatsappAPI.connect(channel.channel_id, { params: { reset_pairing: resetPairing } });
      if (resetPairing) {
        setWaResetCooldown(true);
        if (waResetCooldownTimerRef.current) clearTimeout(waResetCooldownTimerRef.current);
        waResetCooldownTimerRef.current = setTimeout(() => {
          setWaResetCooldown(false);
          waResetCooldownTimerRef.current = null;
        }, 60000);
      }
      startQrPolling(channel.channel_id);
    } catch (err) {
      toast.error('Error conectando al bridge. Verifica que el servicio este activo.');
    }
  };

  const openResetWaPairingConfirm = (channel) => {
    setConfirmState({
      open: true,
      title: 'Reiniciar emparejamiento WhatsApp',
      description:
        'Se borraran en el servidor las credenciales Baileys de este canal y se pedira un QR nuevo. No uses esta opcion mientras el telefono te pide confirmar el vinculo: cancelaria el proceso. En el telefono, Ajustes > Dispositivos vinculados: si aparece un dispositivo vinculado a OmniSyS o al navegador antiguo, eliminalo antes de volver a escanear.',
      confirmText: 'Reiniciar y nuevo QR',
      onConfirm: async () => {
        if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
        setQrPolling(false);
        await handleConnect(channel, { resetPairing: true });
      },
    });
  };

  const startQrPolling = (channelId) => {
    if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
    setQrPolling(true);
    setWaPairingPhase2(false);
    let attempts = 0;

    qrIntervalRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 180) {
        clearInterval(qrIntervalRef.current);
        setQrPolling(false);
        setWaPairingPhase2(false);
        toast.error('Timeout esperando conexion WhatsApp (paso QR o confirmacion en telefono)');
        return;
      }

      try {
        const res = await whatsappAPI.getQR(channelId);
        const data = res.data;

        if (data.status === 'pairing_confirm_phone') {
          setWaPairingPhase2(true);
          setQrData(null);
        } else if (data.status === 'qr_ready' && data.qr) {
          setWaPairingPhase2(false);
          setQrData(data.qr);
        } else if (data.status === 'open') {
          clearInterval(qrIntervalRef.current);
          setQrPolling(false);
          setWaPairingPhase2(false);
          setQrData(null);
          toast.success(`WhatsApp conectado: ${data.phone}`);
          loadChannels();
        }
      } catch (err) { /* continue polling */ }
    }, 2000);
  };

  const handleDisconnect = async (channel) => {
    try {
      await whatsappAPI.disconnect(channel.channel_id);
      toast.success('WhatsApp desconectado');
      loadChannels();
      if (selectedChannel?.channel_id === channel.channel_id) {
        setQrData(null);
        setWaPairingPhase2(false);
        setQrPolling(false);
        if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
      }
    } catch (err) { toast.error('Error desconectando'); }
  };

  const parseAllowedGroupJids = (input) => {
    const parts = String(input || '')
      .split(/[\n,]/g)
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
    return [...new Set(parts)];
  };

  const handleSaveWaGroupPolicy = async () => {
    if (!selectedChannel || selectedChannel.channel_type !== 'whatsapp') return;
    const allowed_group_jids = parseAllowedGroupJids(waAllowedGroupJidsText);
    const idleN = parseInt(waItIdleMs, 10);
    const maxBatchN = parseInt(waItMaxBatchMs, 10);
    const inbound_turn = {
      mode: waItMode,
      idle_quiet_ms: Number.isFinite(idleN) ? idleN : 3500,
      max_batch_window_ms: Number.isFinite(maxBatchN) ? maxBatchN : 30000,
      merge_user_text: waItMerge,
      group_trigger: waItGroupTrigger,
      debounce_backend: waItDebounceBackend,
    };
    setWaPolicySaving(true);
    try {
      const nextConfig = {
        ...(selectedChannel.config || {}),
        group_policy: waGroupPolicy,
        allowed_group_jids,
        inbound_turn,
      };
      const res = await channelsAPI.update(selectedChannel.channel_id, { config: nextConfig });
      const updatedChannel = res.data;
      setSelectedChannel(updatedChannel);
      setChannels((prev) => prev.map((ch) => (ch.channel_id === updatedChannel.channel_id ? updatedChannel : ch)));
      setWaAllowedGroupJidsText(Array.isArray(updatedChannel.config?.allowed_group_jids) ? updatedChannel.config.allowed_group_jids.join('\n') : '');
      setWaGroupPolicy(updatedChannel.config?.group_policy || 'deny_all');
      const uit = updatedChannel.config?.inbound_turn || {};
      setWaItMode(uit.mode || 'immediate');
      setWaItIdleMs(String(uit.idle_quiet_ms ?? 3500));
      setWaItMaxBatchMs(String(uit.max_batch_window_ms ?? 30000));
      setWaItMerge(uit.merge_user_text || 'concat_newlines');
      setWaItGroupTrigger(uit.group_trigger || 'every_message');
      setWaItDebounceBackend(uit.debounce_backend || 'memory');
      toast.success('Politica de canal WhatsApp actualizada');
    } catch (err) {
      toast.error('No se pudo guardar la politica del canal');
    } finally {
      setWaPolicySaving(false);
    }
  };

  const refreshStatus = async (channel) => {
    if (channel.channel_type === 'whatsapp') {
      try {
        const res = await whatsappAPI.getStatus(channel.channel_id);
        const status = res.data.status || 'disconnected';
        // **Lo que se pudo enviar de verdad viaja con el estado.** `connected` solo dice que
        // Baileys cree que hay socket, y el 2026-08-24 eso fue cierto durante horas con el canal
        // muerto: la pantalla verde y ningun mensaje saliendo.
        const salud = {
          last_send_ok_at: res.data.last_send_ok_at || null,
          last_send_error: res.data.last_send_error || null,
        };
        setChannels(prev => prev.map(c =>
          c.channel_id === channel.channel_id ? { ...c, status, ...salud, phone_number: res.data.phone || c.phone_number } : c
        ));
        if (selectedChannel?.channel_id === channel.channel_id) {
          setSelectedChannel(prev => prev ? { ...prev, status, ...salud, phone_number: res.data.phone || prev.phone_number } : prev);
        }
      } catch (err) { /* ignore */ }
      return;
    }
    if (channel.channel_type === 'teams' || channel.channel_type === 'email') {
      try {
        const res = await channelsAPI.getGraphStatus(channel.channel_id);
        const status = res.data.status || 'disconnected';
        setChannels(prev => prev.map(c =>
          c.channel_id === channel.channel_id ? { ...c, status, graph_meta: res.data.graph_meta } : c
        ));
        if (selectedChannel?.channel_id === channel.channel_id) {
          setSelectedChannel(prev => prev ? { ...prev, status, graph_meta: res.data.graph_meta } : prev);
        }
      } catch (err) { /* ignore */ }
    }
  };

  const handleChannelUpdated = (recibido) => {
    // Las respuestas de editar el canal no traen quién lo atiende (lo arma la lista): se conserva.
    const previo = channels.find(c => c.channel_id === recibido.channel_id);
    const updated = { agentes_que_atienden: previo?.agentes_que_atienden, ...recibido };
    setSelectedChannel(updated);
    setChannels(prev => prev.map(c => c.channel_id === updated.channel_id ? updated : c));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="channels-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[#09090B]">CANALES</h1>
          <p className="text-xs text-zinc-400 mt-1 uppercase tracking-[0.15em]">
            Gestiona conexiones omnicanal: WhatsApp, Teams, Email, WebChat
          </p>
        </div>
        <div className="flex gap-2">
          <HelpButton section="channels" />
          <Button
            data-testid="create-channel-button"
          onClick={() => setShowCreate(!showCreate)}
          className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-9"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuevo Canal
        </Button>
        </div>
      </div>

      {/* Create channel form */}
      {showCreate && (
        <Card className="border border-brand-primary/30 bg-blue-50/20 rounded-sm mb-6" data-testid="create-channel-form">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-3 uppercase tracking-[0.1em]">Nuevo Canal</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Nombre</Label>
                <Input
                  data-testid="channel-name-input"
                  value={newChannel.name}
                  onChange={e => setNewChannel(p => ({ ...p, name: e.target.value }))}
                  placeholder="ej: Soporte WhatsApp"
                  className="rounded-sm text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <select
                  data-testid="channel-type-select"
                  value={newChannel.channel_type}
                  onChange={e => setNewChannel(p => ({ ...p, channel_type: e.target.value }))}
                  className="w-full border border-brand-border rounded-sm p-2 text-sm h-10"
                >
                  {Object.entries(CHANNEL_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">
                  {(newChannel.channel_type === 'teams' || newChannel.channel_type === 'email')
                    ? 'Cuenta Microsoft (UPN)'
                    : 'Numero (opcional)'}
                </Label>
                {(newChannel.channel_type === 'teams' || newChannel.channel_type === 'email') ? (
                  <SelectorDeCasilla
                    testId="channel-ms-account-input"
                    channelType={newChannel.channel_type}
                    channelId={null}
                    valor={newChannel.config.microsoft_account || ''}
                    onChange={v => setNewChannel(p => ({ ...p, config: { ...p.config, microsoft_account: v } }))}
                  />
                ) : (
                  <Input
                    data-testid="channel-phone-input"
                    value={newChannel.config.phone_number}
                    onChange={e => setNewChannel(p => ({ ...p, config: { ...p.config, phone_number: e.target.value } }))}
                    placeholder="+5491155551234"
                    className="rounded-sm text-sm"
                  />
                )}
                {(newChannel.channel_type === 'teams' || newChannel.channel_type === 'email') && (
                  <p className="text-[10px] text-zinc-500 mt-1">
                    Arranca revisando la casilla cada 2 minutos, desde que lo creás: lo que ya estaba no se contesta.
                    Se cambia después en «Cómo entra lo nuevo».
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">Cápsula (obligatorio)</Label>
                <select
                  data-testid="channel-capsule-select"
                  value={newChannel.config.capsule_id}
                  onChange={e => setNewChannel(p => ({ ...p, config: { ...p.config, capsule_id: e.target.value } }))}
                  className="w-full border border-brand-border rounded-sm p-2 text-sm h-10 bg-white"
                >
                  <option value="">— Elegir —</option>
                  {capsuleList.map((c) => (
                    <option key={c.capsule_id} value={c.capsule_id}>{c.capsule_id}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  data-testid="save-channel-button"
                  onClick={handleCreateChannel}
                  className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-10 w-full"
                >
                  Crear Canal
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Channel list */}
        <div className="col-span-5">
          <Card className="border border-brand-border rounded-sm">
            <CardHeader className="pb-2 border-b border-brand-border">
              <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                <Radio className="w-3.5 h-3.5 inline mr-1.5" />
                {channels.length} Canales Registrados
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {channels.length === 0 ? (
                <p className="text-xs text-zinc-400 text-center py-8">No hay canales. Crea uno nuevo.</p>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {channels.map(ch => {
                    const typeInfo = CHANNEL_TYPES[ch.channel_type] || CHANNEL_TYPES.webchat;
                    const statusInfo = STATUS_MAP[ch.status] || STATUS_MAP.disconnected;
                    const StatusIcon = statusInfo.icon;
                    const isSelected = selectedChannel?.channel_id === ch.channel_id;

                    return (
                      <button
                        key={ch.channel_id}
                        data-testid={`channel-item-${ch.channel_id}`}
                        className={`w-full text-left p-3 transition-colors hover:bg-zinc-50 ${isSelected ? 'bg-blue-50/50 border-l-2 border-brand-primary' : ''}`}
                        onClick={() => setSelectedChannel(ch)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${ch.status === 'connected' ? 'bg-green-500' : 'bg-zinc-300'}`} />
                            <span className="text-sm font-medium text-[#09090B]">{ch.name}</span>
                          </div>
                          <Badge variant="outline" className="text-[9px] rounded-sm">{typeInfo.label}</Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 pl-4">
                          <StatusIcon className={`w-3 h-3 ${statusInfo.color} ${ch.status === 'connecting' ? 'animate-spin' : ''}`} />
                          <span className={`text-[10px] ${statusInfo.color}`}>{statusInfo.label}</span>
                          {ch.is_active === false && (
                            <span className="text-[10px] text-zinc-500">· Pausado</span>
                          )}
                          {nadieLoAtiende(ch) && (
                            <span className="text-[10px] text-amber-700" data-testid={`canal-sin-agente-${ch.channel_id}`}>· Ningún agente lo atiende</span>
                          )}
                          {ch.phone_number && (
                            <span className="text-[10px] font-mono text-zinc-400 ml-auto">{ch.phone_number}</span>
                          )}
                          {ch.config?.microsoft_account && (
                            <span className="text-[10px] font-mono text-zinc-400 ml-auto truncate max-w-[140px]">{ch.config.microsoft_account}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Channel detail / QR */}
        <div className="col-span-7">
          {!selectedChannel ? (
            <Card className="border border-brand-border rounded-sm">
              <CardContent className="p-8 text-center">
                <Settings className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-xs text-zinc-400">Selecciona un canal para ver detalles y conectar</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-brand-border rounded-sm" data-testid="channel-detail">
              <CardHeader className="pb-3 border-b border-brand-border">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#09090B]">
                    {selectedChannel.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      data-testid="refresh-status-button"
                      variant="outline"
                      size="sm"
                      onClick={() => refreshStatus(selectedChannel)}
                      className="rounded-sm text-xs h-8"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" /> Estado
                    </Button>
                    <Button
                      data-testid="delete-channel-button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteChannel(selectedChannel.channel_id)}
                      className="rounded-sm text-xs h-8 text-red-500 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <AtencionDelCanal channel={selectedChannel} onChannelUpdated={handleChannelUpdated} />

                {/* Channel info */}
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <Label className="text-[10px] text-zinc-400 uppercase">Tipo</Label>
                    <p className="font-mono">{selectedChannel.channel_type}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] text-zinc-400 uppercase">
                      {(selectedChannel.channel_type === 'teams' || selectedChannel.channel_type === 'email') ? 'Cuenta MS' : 'Telefono'}
                    </Label>
                    <p className="font-mono text-[11px] break-all">
                      {(selectedChannel.channel_type === 'teams' || selectedChannel.channel_type === 'email')
                        ? (selectedChannel.config?.microsoft_account || 'Sin configurar')
                        : (selectedChannel.phone_number || 'Sin vincular')}
                    </p>
                  </div>
                  <div>
                    <Label className="text-[10px] text-zinc-400 uppercase">Estado</Label>
                    <p className={`font-mono ${(STATUS_MAP[selectedChannel.status] || STATUS_MAP.disconnected).color}`}>
                      {(STATUS_MAP[selectedChannel.status] || STATUS_MAP.disconnected).label}
                    </p>
                  </div>
                </div>

                {/* WhatsApp specific controls */}
                {selectedChannel.channel_type === 'whatsapp' && (
                  <div className="border border-brand-border rounded-sm p-4 space-y-4">
                    <h4 className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-green-600" /> Conexion WhatsApp (Baileys)
                    </h4>

                    {selectedChannel.status === 'connected' ? (
                      <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-green-600">
                          <Wifi className="w-4 h-4" />
                          <span className="text-sm font-medium">Conectado</span>
                          <span className="text-xs font-mono text-zinc-500">{selectedChannel.phone_number}</span>
                        </div>
                        <Button
                          data-testid="disconnect-wa-button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDisconnect(selectedChannel)}
                          className="rounded-sm text-xs h-8 text-red-500 border-red-200 hover:bg-red-50 ml-auto"
                        >
                          <WifiOff className="w-3 h-3 mr-1" /> Desconectar
                        </Button>
                      </div>
                      <UltimoEnvio salud={selectedChannel} />
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-sm text-xs h-9 border-amber-200 text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                            data-testid="reset-wa-pairing-button"
                            disabled={waResetCooldown}
                            title={waResetCooldown ? 'Espera 1 min desde el ultimo reinicio de QR' : undefined}
                            onClick={() => openResetWaPairingConfirm(selectedChannel)}
                          >
                            {waResetCooldown ? 'Reinicio reciente…' : 'Reiniciar QR (limpiar servidor)'}
                          </Button>
                          <Button
                            data-testid="connect-wa-button"
                            onClick={() => handleConnect(selectedChannel)}
                            disabled={qrPolling}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-sm text-xs h-9"
                          >
                            {qrPolling ? (
                              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{waPairingPhase2 ? 'Esperando confirmacion…' : 'Esperando QR...'}</>
                            ) : (
                              <><QrCode className="w-3.5 h-3.5 mr-1.5" /> Conectar WhatsApp</>
                            )}
                          </Button>
                        </div>

                        {waPairingPhase2 && (
                          <div
                            className="flex flex-col gap-2 p-4 bg-blue-50 border border-blue-200 rounded-sm text-sm text-blue-950"
                            data-testid="wa-pairing-phase2"
                          >
                            <p className="font-semibold">Paso 2: confirma en el telefono</p>
                            <p className="text-xs leading-relaxed">
                              El primer escaneo ya se registro. Abre <strong>WhatsApp</strong> en el movil, ve a
                              {' '}<strong>Ajustes / Dispositivos vinculados</strong> y pulsa <strong>Vincular</strong> o
                              confirma el aviso pendiente. <strong>No pulses de nuevo &quot;Conectar WhatsApp&quot;</strong>,
                              {' '}<strong>ni &quot;Reiniciar QR&quot;</strong> ni recargues esta pagina hasta que termine.
                            </p>
                          </div>
                        )}

                        {/* QR code display */}
                        {qrData && (
                          <div className="flex flex-col items-center gap-3 p-4 bg-white border border-zinc-200 rounded-sm" data-testid="qr-display">
                            <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">Escanea con WhatsApp</p>
                            <img src={qrData} alt="WhatsApp QR" className="w-64 h-64" />
                            <p className="text-[10px] text-zinc-400">Abre WhatsApp &gt; Menu &gt; Dispositivos vinculados &gt; Vincular dispositivo</p>
                          </div>
                        )}

                        {qrPolling && !qrData && (
                          <div className="flex items-center gap-2 text-xs text-amber-600">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generando codigo QR...
                          </div>
                        )}
                      </>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-xs h-8"
                        disabled={waDiagLoading}
                        onClick={runWaIntegrationDiag}
                        data-testid="wa-integration-diag-btn"
                      >
                        {waDiagLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Consultando...</> : <><Radio className="w-3 h-3 mr-1" /> Diagnostico pipeline WA</>}
                      </Button>
                      <p className="text-[10px] text-zinc-500 self-center">
                        Comprueba backend hacia bridge y contadores de mensajes (upsert / forward).
                      </p>
                    </div>
                    {waDiagResult && (
                      <div className="border border-zinc-200 rounded-sm bg-zinc-50 p-2 max-h-56 overflow-auto">
                        <p className="text-[10px] font-semibold text-zinc-600 mb-1">Ultima respuesta (JSON)</p>
                        <pre className="text-[9px] font-mono whitespace-pre-wrap break-all text-zinc-700">{JSON.stringify(waDiagResult, null, 2)}</pre>
                      </div>
                    )}

                    <div className="border border-zinc-200 rounded-sm p-3 space-y-3 bg-zinc-50/70">
                      <h5 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-600">
                        Politica de grupos (canal)
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase text-zinc-500">Politica</Label>
                          <select
                            value={waGroupPolicy}
                            onChange={(e) => setWaGroupPolicy(e.target.value)}
                            className="w-full border border-brand-border rounded-sm p-2 text-xs h-9 bg-white"
                            data-testid="wa-group-policy-select"
                          >
                            <option value="deny_all">Denegar grupos por defecto</option>
                            <option value="allowlist">Permitir solo grupos de allowlist</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-zinc-500">JIDs de grupos permitidos</Label>
                          <textarea
                            value={waAllowedGroupJidsText}
                            onChange={(e) => setWaAllowedGroupJidsText(e.target.value)}
                            className="w-full border border-brand-border rounded-sm p-2 text-[11px] font-mono bg-white min-h-[90px]"
                            placeholder="1203630xxxxxxxx@g.us"
                            data-testid="wa-group-allowlist-input"
                          />
                          <p className="text-[10px] text-zinc-500 mt-1">Uno por linea (o separados por coma).</p>
                        </div>
                      </div>

                      <div className="border-t border-zinc-200 pt-3 mt-3 space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <h5 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-600">
                            Politica de turnos (humanidad / tokens)
                          </h5>
                          <HelpButton section="channels_whatsapp_inbound" />
                        </div>
                        <p className="text-[10px] text-zinc-500">
                          Controla cuando se invoca el agente tras mensajes entrantes. Debounce agrupa varios mensajes del usuario; en grupos puede exigirse mención al bot.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[10px] uppercase text-zinc-500">Backend de debounce</Label>
                            <select
                              value={waItDebounceBackend}
                              onChange={(e) => setWaItDebounceBackend(e.target.value)}
                              className="w-full border border-brand-border rounded-sm p-2 text-xs h-9 bg-white"
                              data-testid="wa-inbound-debounce-backend-select"
                            >
                              <option value="memory">Memoria (1 instancia)</option>
                              <option value="mongo">Mongo (varias réplicas)</option>
                            </select>
                            <p className="text-[10px] text-zinc-500 mt-1">Con varias réplicas del backend sin sticky sessions, use Mongo.</p>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-zinc-500">Modo</Label>
                            <select
                              value={waItMode}
                              onChange={(e) => setWaItMode(e.target.value)}
                              className="w-full border border-brand-border rounded-sm p-2 text-xs h-9 bg-white"
                              data-testid="wa-inbound-mode-select"
                            >
                              <option value="immediate">Inmediato (cada mensaje)</option>
                              <option value="debounce_idle">Debounce por silencio</option>
                              <option value="debounce_idle_or_typing">Debounce + señal de escritura</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-zinc-500">Silencio antes de responder (ms)</Label>
                            <Input
                              value={waItIdleMs}
                              onChange={(e) => setWaItIdleMs(e.target.value)}
                              type="number"
                              min={500}
                              className="rounded-sm text-xs h-9"
                              data-testid="wa-inbound-idle-ms"
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-zinc-500">Ventana máxima de lote (ms)</Label>
                            <Input
                              value={waItMaxBatchMs}
                              onChange={(e) => setWaItMaxBatchMs(e.target.value)}
                              type="number"
                              min={3000}
                              className="rounded-sm text-xs h-9"
                              data-testid="wa-inbound-max-batch-ms"
                            />
                            <p className="text-[10px] text-zinc-500 mt-1">Tras este tiempo se responde aunque siga la señal de escritura.</p>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase text-zinc-500">Fusionar texto del usuario</Label>
                            <select
                              value={waItMerge}
                              onChange={(e) => setWaItMerge(e.target.value)}
                              className="w-full border border-brand-border rounded-sm p-2 text-xs h-9 bg-white"
                              data-testid="wa-inbound-merge-select"
                            >
                              <option value="concat_newlines">Varios mensajes → un texto (saltos)</option>
                              <option value="last_only">Solo el último mensaje</option>
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-[10px] uppercase text-zinc-500">Disparo en grupos (@mención)</Label>
                            <select
                              value={waItGroupTrigger}
                              onChange={(e) => setWaItGroupTrigger(e.target.value)}
                              className="w-full border border-brand-border rounded-sm p-2 text-xs h-9 bg-white"
                              data-testid="wa-inbound-group-trigger-select"
                            >
                              <option value="every_message">Todos los mensajes</option>
                              <option value="mention_only">Solo si mencionan al bot</option>
                              <option value="never">Nunca responder en grupo (solo 1:1 efectivo)</option>
                            </select>
                          </div>
                        </div>

                        <div className="border border-zinc-200 rounded-sm bg-white">
                          <button
                            type="button"
                            onClick={() => setWaInboundHelpOpen((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
                            data-testid="wa-inbound-help-toggle"
                          >
                            <span>Ayuda: turnos entrantes, debounce y alineación con RULES</span>
                            {waInboundHelpOpen ? (
                              <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                            )}
                          </button>
                          {waInboundHelpOpen && (
                            <div className="px-3 pb-3 text-[10px] text-zinc-600 space-y-2 border-t border-zinc-100">
                              <p>
                                <span className="font-semibold text-zinc-700">Modos (cuándo corre el agente):</span>{' '}
                                <em>Inmediato</em> dispara en cada mensaje elegible. <em>Debounce por silencio</em> espera{' '}
                                <code className="text-[9px] bg-zinc-100 px-1 rounded">idle_quiet_ms</code> tras el último mensaje del usuario antes de agrupar y ejecutar.{' '}
                                <em>Debounce + escritura</em> también considera la señal de &quot;escribiendo&quot; del bridge hasta un tope de{' '}
                                <code className="text-[9px] bg-zinc-100 px-1 rounded">max_batch_window_ms</code>.
                              </p>
                              <p>
                                <span className="font-semibold text-zinc-700">Fusión (<code className="text-[9px] bg-zinc-100 px-1 rounded">merge</code>):</span>{' '}
                                define cómo se unen varios mensajes seguidos del usuario en un solo texto para el modelo (p. ej. concatenar con saltos vs. solo el último).
                              </p>
                              <p>
                                <span className="font-semibold text-zinc-700">Disparo en grupos (<code className="text-[9px] bg-zinc-100 px-1 rounded">group_trigger</code>):</span>{' '}
                                en hilos de grupo puedes exigir mención al bot, procesar todo o nunca responder en grupo (efectivo solo en 1:1).
                              </p>
                              <p>
                                <span className="font-semibold text-zinc-700">Backend de debounce (Mongo vs memoria):</span>{' '}
                                el valor <code className="text-[9px] bg-zinc-100 px-1 rounded">debounce_backend</code> en la politica fusionada (tenant / capsula / canal) decide si el aplazamiento se encola en{' '}
                                <code className="text-[9px] bg-zinc-100 px-1 rounded">wa_inbound_debounce_jobs</code> (recomendado con varias replicas del API) o si se usa un temporizador solo en el proceso actual.
                              </p>
                              <p>
                                <span className="font-semibold text-zinc-700">Alineación RULES vs. inbound_turn:</span>{' '}
                                <code className="text-[9px] bg-zinc-100 px-1 rounded">inbound_turn</code> es la máquina de &quot;cuándo&quot; se permite ejecutar el agente (debounce, grupos, menciones, escritura).{' '}
                                Las <em>RULES</em> del agente guían tono, herramientas y límites cuando el agente ya corre. Conviene que ambos criterios estén alineados (p. ej. no prometer en RULES un estilo si el gating de turnos retrasa o bloquea respuestas en grupo).
                              </p>
                              <p className="text-zinc-500">
                                Mas detalle: boton Ayuda arriba (modal <code className="text-[9px] bg-zinc-100 px-1 rounded">channels_whatsapp_inbound</code>).
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          onClick={handleSaveWaGroupPolicy}
                          disabled={waPolicySaving}
                          className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-8"
                          data-testid="wa-group-policy-save-btn"
                        >
                          {waPolicySaving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Guardando...</> : <><Save className="w-3 h-3 mr-1" /> Guardar politica WhatsApp</>}
                        </Button>
                        <p className="text-[10px] text-zinc-500">
                          Incluye grupos permitidos y politica de turnos. Si el grupo no esta permitido, se bloquea inbound y outbound.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-sm">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="text-[10px] text-amber-700">
                        <p className="font-medium">Nota sobre Baileys (WebChat scraping)</p>
                        <p>Esta conexion usa el protocolo de WhatsApp Web. El telefono debe mantener conexion a internet. No es la API oficial de Meta.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Microsoft Teams / Email: el canal dice **que casilla**, y nada mas.
                    Las credenciales de Azure salieron de aca el 2026-08-22: viven en la conexion
                    `microsoft-365`, que es donde se cargan, se rotan y se prueban. Tenerlas en
                    dos pantallas que no se conocen era el enredo que Diego marco, y guardar
                    desde aca recreaba la copia que los puntos 9 y 10 acababan de borrar. */}
                {(selectedChannel.channel_type === 'teams' || selectedChannel.channel_type === 'email') && (
                  <>
                    <MicrosoftChannelAccountPanel channel={selectedChannel} onChannelUpdated={handleChannelUpdated} />
                    {/* Teams lee y contesta como la cuenta, conectada una vez (2026-10-19). */}
                    {selectedChannel.channel_type === 'teams' && (
                      <CuentaConectada key={selectedChannel.channel_id} channel={selectedChannel} />
                    )}
                    {/* Barrido o aviso, el estado y traer lo anterior (2026-10-19). */}
                    <IngresoDeM365 channel={selectedChannel} onChannelUpdated={handleChannelUpdated} />
                  </>
                )}

                {/* A quién atiende el canal: el corte antes de crear el contacto (2026-10-14). */}
                {['whatsapp', 'teams', 'email'].includes(selectedChannel.channel_type) && (
                  <QuienAtiendeElCanal channel={selectedChannel} onChannelUpdated={handleChannelUpdated} />
                )}

                {/* Channel ID for reference */}
                <div className="pt-2 border-t border-zinc-100">
                  <Label className="text-[10px] text-zinc-400 uppercase">Channel ID</Label>
                  <p className="text-[10px] font-mono text-zinc-500">{selectedChannel.channel_id}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <ConfirmActionDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) setConfirmState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onConfirm={confirmState.onConfirm}
      />
    </div>
  );
}
